import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, FileText, LoaderCircle } from 'lucide-react';
import { voiceTranscript } from '../api/client';
import { voiceTranscriptError, type VoiceContext } from '../lib/voiceTranscript';
import '../styles/voice-transcript.css';

export function VoiceTranscript({ url, context }: { url: string; context: VoiceContext }) {
  const client = useQueryClient();
  const key = ['voice-transcript', context, url];
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => voiceTranscript(url, context, false, signal),
    retry: false,
    staleTime: 60_000,
    refetchInterval: query => ['pending', 'processing'].includes(query.state.data?.status ?? '') ? 3000 : false,
  });
  const start = useMutation({
    mutationFn: () => voiceTranscript(url, context, true),
    onSuccess: data => { client.setQueryData(key, data); setExpanded(true); },
  });
  const result = query.data;
  const busy = start.isPending || result?.status === 'pending' || result?.status === 'processing';
  const ready = result?.status === 'ready';
  const label = result?.status === 'pending' ? 'В очереди…' : result?.status === 'processing' || start.isPending ? 'Распознаём…' : ready ? expanded ? 'Скрыть текст' : 'Показать текст' : 'Расшифровать';
  const error = start.error?.message || (result?.status === 'error' ? voiceTranscriptError(result.error) : '');
  return <div className="voice-transcript">
    <button className="voice-transcript__toggle" type="button" title={label} aria-label={label} disabled={busy || query.isPending} aria-expanded={ready ? expanded : undefined} onClick={() => ready ? setExpanded(!expanded) : start.mutate()}>
      {busy ? <LoaderCircle size={18} className="spin"/> : <FileText size={18}/>}
    </button>
    {expanded && ready && <div className="voice-transcript__text"><p>{result.text || 'Речь не распознана.'}</p>{result.text && <button type="button" title={copied ? 'Скопировано' : 'Копировать текст'} aria-label={copied ? 'Скопировано' : 'Копировать расшифровку'} onClick={() => { void (async () => { try { await navigator.clipboard.writeText(result.text); setCopied(true); setCopyError(false); } catch { setCopyError(true); } })(); }}><Copy size={16}/>{copied && <span>Скопировано</span>}</button>}<small>Автоматическая расшифровка — возможны ошибки.</small></div>}
    {error && <small role="status">{error}</small>}
    {copyError && <small role="status">Выделите текст и скопируйте вручную.</small>}
  </div>;
}
